import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSliderData } from "../features/Slider/SliderSlice";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";

function Slider({ settings }) {
  const dispatch = useDispatch();
  const { slides, loading } = useSelector((state) => state.slider);
  const [selectedType, setSelectedType] = useState("all");
  const { i18n } = useTranslation();
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(0);
  
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useEffect(() => {
    dispatch(fetchSliderData({ type: selectedType }));
    setPrevIndex(0);
    setCurrentIndex(0);
  }, [dispatch, i18n.language, selectedType]);

  const handleChange = (type) => {
    if (type === selectedType) {
      setSelectedType(""); // Reset to the first slide if the same type is selected
      return; // Prevent unnecessary dispatch if the type hasn't changed
    }; // Prevent unnecessary dispatch
    setSelectedType(type);
  };

  const isRTL = localStorage.getItem("lang") === "ar" || localStorage.getItem("lang") === "ckb";

  const nextSlide = useCallback(() => {
    if (!slides || slides.length === 0) return;
    setPrevIndex(currentIndex);
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  }, [slides, currentIndex]);

  const prevSlide = useCallback(() => {
    if (!slides || slides.length === 0) return;
    setPrevIndex(currentIndex);
    setCurrentIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  }, [slides, currentIndex]);

  // Auto-play
  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide();
    }, 4000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") isRTL ? nextSlide() : prevSlide();
      if (e.key === "ArrowRight") isRTL ? prevSlide() : nextSlide();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide, isRTL]);

  // Touch Swipe Handlers
  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };
  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };
  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const diff = touchStartX.current - touchEndX.current;
    if (diff > 50) {
      isRTL ? prevSlide() : nextSlide(); // Swiped left
    }
    if (diff < -50) {
      isRTL ? nextSlide() : prevSlide(); // Swiped right
    }
    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  return (
    <div className="w-full flex flex-col items-center  relative">
      <style>{`
        @keyframes mosaicReveal {
          0% { transform: scale(0.3) rotate(5deg); opacity: 0; }
          100% { transform: scale(1) rotate(0); opacity: 1; }
        }
        @keyframes showSolid {
          to { opacity: 1; }
        }
      `}</style>
      {/* Main Slider Container */}
      <div 
        className="relative w-full aspect-video md:aspect-auto md:h-[85vh] md:min-h-[600px] rounded-xl md:rounded-2xl overflow-hidden group shadow-2xl bg-gray-100 dark:bg-[#0a0a0a]"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Full-Screen Premium Skeleton for Initial Load */}
        {loading && (!slides || slides.length === 0) && (
          <div className="absolute inset-0 w-full h-full z-50 flex flex-col justify-center items-start p-4 md:p-16 lg:p-24 bg-gray-200 dark:bg-[#0f0f0f] animate-pulse">
            {/* Fake Content Bars */}
            <div className="w-3/4 sm:w-2/3 md:w-1/2 h-8 md:h-16 bg-gray-300 dark:bg-zinc-800 rounded-lg mb-4 md:mb-6 mt-1 md:mt-0"></div>
            <div className="w-full md:w-3/4 h-3 md:h-5 bg-gray-300 dark:bg-zinc-800 rounded-md mb-2 md:mb-3"></div>
            <div className="w-5/6 md:w-2/3 h-3 md:h-5 bg-gray-300 dark:bg-zinc-800 rounded-md mb-2 md:mb-3"></div>
            <div className="w-4/6 md:w-1/2 h-3 md:h-5 bg-gray-300 dark:bg-zinc-800 rounded-md"></div>
            
            {/* Centered Spinner */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-gray-300 dark:border-white/10 rounded-full"></div>
                <div className="w-12 h-12 border-4 border-transparent border-t-green-600 rounded-full animate-spin absolute"></div>
              </div>
            </div>
          </div>
        )}

        {/* Subtle Overlay Spinner for Category Switching */}
        {loading && slides?.length > 0 && (
          <div className="absolute inset-0 w-full h-full z-50 bg-white/20 dark:bg-black/40 backdrop-blur-sm flex items-center justify-center transition-all duration-300">
             <div className="w-10 h-10 border-4 border-gray-200/50 dark:border-white/20 border-t-green-600 rounded-full animate-spin"></div>
          </div>
        )}

        {slides?.length > 0 && slides.map((slide, slideIndex) => {
          const isActive = currentIndex === slideIndex;
          const isPrev = prevIndex === slideIndex;

          return (
            <div
              key={slide.id || slideIndex}
              className={`absolute inset-0 w-full h-full ${
                isActive ? 'z-20 opacity-100' : isPrev ? 'z-10 opacity-100' : 'z-0 opacity-0 pointer-events-none'
              }`}
            >
              {/* Solid Image - ALWAYS rendered to prevent DOM unmount flickering. 
                  Fades in at the end of mosaic animation if active, otherwise stays solid. */}
              <LazyLoadImage
                src={process.env.REACT_APP_IMG_URL + slide.image}
                alt=""
                className="absolute inset-0 w-full h-full object-cover z-0"
                style={{ 
                  opacity: isActive ? 0 : 1, 
                  animation: isActive ? 'showSolid 0.1s 1.3s forwards' : 'none' 
                }}
              />

              {isActive && (
                <div className="absolute inset-0 w-full h-full z-0 overflow-hidden">
                  {Array.from({ length: 48 }).map((_, i) => {
                    const cols = 8;
                    const rows = 6;
                    const col = i % cols;
                    const row = Math.floor(i / cols);
                    
                    const widthPercent = 100 / cols;
                    const heightPercent = 100 / rows;
                    
                    const randomHash = Math.abs(Math.sin((i + 1) * 7.13) * 123.45) % 1;
                    const delay = randomHash * 0.7;

                    return (
                      <div 
                        key={i} 
                        className="absolute overflow-hidden"
                        style={{ 
                          width: `${widthPercent}%`,
                          height: `${heightPercent}%`,
                          left: `${col * widthPercent}%`,
                          top: `${row * heightPercent}%`,
                          animation: `mosaicReveal 0.6s cubic-bezier(0.25, 1, 0.5, 1) ${delay}s both`
                        }}
                      >
                        <div 
                          className="absolute"
                          style={{ 
                            width: `${cols * 100}%`,
                            height: `${rows * 100}%`,
                            left: `-${col * 100}%`,
                            top: `-${row * 100}%`
                          }}
                        >
                          {/* Standard img tag instead of LazyLoadImage to prevent IntersectionObserver flicker */}
                          <img
                            src={process.env.REACT_APP_IMG_URL + slide.image}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Dark background overlay for text contrast */}
              <div className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent md:from-black/40 md:via-black/10 md:to-transparent z-0 transition-opacity duration-700 ${isActive ? 'opacity-100' : 'opacity-0'}`} />
              
              {/* Content Overlay */}
              <div className={`absolute inset-0 flex flex-col justify-center items-start p-4 md:p-16 lg:p-24 w-full lg:w-3/4 mt-1 md:mt-0 transition-all duration-700 delay-300 ${isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <h2
                  className="text-white text-lg sm:text-2xl md:text-5xl lg:text-6xl font-bold leading-tight mb-1 md:mb-6 drop-shadow-md"
                  dangerouslySetInnerHTML={{ __html: slide.main_text }}
                />
                
                <p
                  className="text-white/90 text-[10px] sm:text-xs md:text-lg lg:text-xl font-light mb-4 md:mb-12 max-w-2xl drop-shadow-sm"
                  dangerouslySetInnerHTML={{
                    __html: slide.description?.length > 150
                      ? slide.description.slice(0, 150) + '...'
                      : slide.description
                  }}
                />
              </div>
            </div>
          );
        })}

        {/* Indicators at the bottom corner (adapts to LTR/RTL) */}
        <div className={`absolute bottom-6 md:bottom-12 z-20 flex gap-2 items-center ${isRTL ? 'left-6 md:left-16' : 'right-6 md:right-16'}`}>
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              aria-label={`Go to slide ${idx + 1}`}
              className={`h-1.5 transition-all duration-300 rounded-full cursor-pointer ${
                currentIndex === idx ? "w-8 bg-white" : "w-2 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>

        {/* Floating Navigation Arrows */}
        <button
          onClick={isRTL ? prevSlide : nextSlide}
          className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 
                     w-8 h-8 md:w-10 md:h-10 flex items-center justify-center 
                     rounded-full bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-md 
                     text-white opacity-100 md:opacity-0 group-hover:opacity-100 transition-all duration-300 cursor-pointer shadow-sm"
          aria-label="Next Slide"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 md:h-5 md:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
        
        <button
          onClick={isRTL ? nextSlide : prevSlide}
          className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 
                     w-8 h-8 md:w-10 md:h-10 flex items-center justify-center 
                     rounded-full bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-md 
                     text-white opacity-100 md:opacity-0 group-hover:opacity-100 transition-all duration-300 cursor-pointer shadow-sm"
          aria-label="Previous Slide"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 md:h-5 md:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {/* Floating Bottom Filter & Search Bar */}
      <div className="absolute -bottom-6 md:-bottom-8 left-1/2 -translate-x-1/2 w-[95%] md:w-11/12 lg:w-1/2 max-w-8xl z-30">
        {/* Premium Frosted Glass Layer */}
        <div className="bg-white/20 dark:bg-black/20 backdrop-blur-md rounded-xl p-1.5 md:p-2 shadow-[0_8px_32px_rgba(0,0,0,0.12)] border border-white/40 dark:border-white/10 flex flex-row items-center transition-all duration-300">
          
          {/* Category Pill Filters */}
          <div className="overflow-x-auto flex justify-start lg:justify-center items-center gap-1.5 md:gap-2 w-full scroll-smooth [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {settings?.map((item, index) => (
              <button
                key={index}
                disabled={loading}
                onClick={() => handleChange(item.value)}
                className={`whitespace-nowrap px-4 py-1.5 md:px-6 md:py-2.5  rounded-md text-[12px] md:text-sm font-medium transition-all duration-300 shrink-0 shadow-sm ${
                  selectedType === item.value 
                    ? 'bg-green-600 text-white scale-100 md:scale-105 border border-green-500/50' 
                    : 'bg-white/60 dark:bg-black/40 text-zinc-800 dark:text-zinc-200 hover:bg-white/90 dark:hover:bg-black/60 border border-white/50 dark:border-zinc-700/50'
                } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {item.value}
              </button>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}

export default Slider;
